import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useContext, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { z } from "zod";
import { AuthRepository } from "@/api/auth/repository";
import { AuthService } from "@/api/auth/service";
import { InputControl } from "@/components/controls";
import { ROUTES } from "@/router";
import { Button } from "@/ui-kit";
import { AuthPageContext } from "../../context/AuthPageContext";
import { FormView } from "../FormView";

type IForm = {
  email: string;
};

const ForgotPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const { usernameRef } = useContext(AuthPageContext);
  const [emailSent, setEmailSent] = useState<boolean>(false);

  const authRepository = useMemo(() => {
    return new AuthRepository(new AuthService());
  }, []);

  const schema = useMemo(() => {
    return z.object({
      email: z.string().min(1, { message: t("validation.required") }),
    });
  }, [t]);

  const form = useForm<IForm>({
    defaultValues: {
      email: usernameRef.current || "",
    },
    resolver: zodResolver(schema),
  });
  const { handleSubmit, formState, setError } = form;

  const onSubmit = useCallback(
    (formData: IForm) => {
      setLoading(true);
      usernameRef.current = formData.email;

      authRepository
        .forgotPassword({
          email: formData.email,
        })
        .then(() => {
          setEmailSent(true);
        })
        .catch(() => {
          setError("email", {
            message: t("label.emailNotRegistered"),
          });
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [authRepository, setError, t, usernameRef],
  );

  const handleBackClick = useCallback(() => {
    navigate(`/${ROUTES.AUTH}/${ROUTES.SIGN_IN}`);
  }, [navigate]);

  return (
    <FormProvider {...form}>
      {emailSent ? (
        <FormView title={t("label.forgotPasswordEmailSent")}>
          <Button
            variant="soft"
            type="button"
            size="lg"
            color="neutral"
            content={t("action.backToLogin")}
            onClick={handleBackClick}
          />
        </FormView>
      ) : (
        <FormView
          title={t("label.forgotPassword")}
          subtitle={t("label.enterEmailForResetLink")}
        >
          <form
            className="nuco-auth__common-form"
            onSubmit={handleSubmit(onSubmit)}
          >
            <InputControl
              name="email"
              size="lg"
              label={t("label.email")}
              placeholder={t("placeholder.enterEmail")}
              invalid={!!formState.errors.email}
              autoFocus={true}
            />

            <div className="nuco-auth__common-form-actions">
              <Button
                type="submit"
                size="lg"
                content={t("action.continue")}
                loading={loading}
              />

              <Button
                variant="soft"
                type="button"
                size="lg"
                color="neutral"
                content={t("action.back")}
                onClick={handleBackClick}
              />
            </div>
          </form>
        </FormView>
      )}
    </FormProvider>
  );
};

export default ForgotPassword;
